import Badge from '@components/Badge';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { CouponRow } from '@models/coupon';
import { Link } from '@stackflow/link';
import { pressActionEffect } from '@styles/mixin';
import dayjs from 'dayjs';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
    coupon:CouponRow;
    isHistory?: boolean;
}

function CouponItem({ coupon, isHistory }:Props) {
    const {
        expiresAt, availableShops, couponName, imageUrl, status, couponId,
        tierInfo,
    } = coupon;
    const { t } = useTranslation('coupon');
    const leftDays = dayjs(expiresAt)
        .subtract(1, 'second')
        .diff(dayjs(), 'day');
    const headerText = (() => {
        switch (status) {
        case 'USED': return t('status.used');
        case 'EXPIRED': return t('status.expired');
        case 'CANCELLED': return t('status.cancelled');
        default:
            if (leftDays === 0) {
                return t('status.expiresToday');
            }
            // MY쿠폰은 D-day, 히스토리는 남은 일수로 표시
            return isHistory
                ? t('status.daysLeft', { days: leftDays })
                : t('status.dDay', { days: leftDays });
        }
    })();

    return (
        <Link activityName='CouponDetailActivity'
            aria-label='coupon-item'
            data-coupon-name={couponName}
            data-coupon-id={couponId}
            data-available-shops={availableShops}
            activityParams={{ couponId }}
            css={wrapperCss} >
            <Container fullWidth spacing={4} align='center'>
                <ImageWrapper>
                    <Thumbnail src={imageUrl} alt={couponName} width={80} height={80} />
                </ImageWrapper>
                <TextContainer spacing={1}>
                    {/* 등급제를 통해 받은 쿠폰일 경우 표시 */}
                    {tierInfo?.displayType === 'BADGE' && (
                        <TierBadge color={tierInfo.textColor}
                            backgroundColor={tierInfo.backgroundColor} >
                            {t('tierBenefit', { name: tierInfo.tierName })}
                        </TierBadge>
                    )}
                    {tierInfo?.displayType === 'ICON' && (
                        <Row spacing={1}>
                            <Image src={tierInfo.iconImageUrl}
                                width={16}
                                height={16} />
                            <Text size='label-lg' fontWeight='bold'>
                                {t('tierBenefit', { name: tierInfo.tierName })}
                            </Text>
                        </Row>
                    )}
                    {availableShops !== '전체매장' && (
                        <Text size='body-long' ellipsis>{availableShops}</Text>
                    )}
                    <Text size='title' fontWeight='medium' ellipsis>{couponName}</Text>
                    <Row spacing={2}>
                        <Text color='var(--color-text-secondary)' size='body-long'>
                            {t('validity', {
                                date: dayjs(expiresAt)
                                    .subtract(1, 'second')
                                    .format('YYYY.MM.DD'),
                            })}
                        </Text>
                        <Badge>{headerText}</Badge>
                    </Row>
                </TextContainer>
            </Container>
        </Link>
    );
}

const Container = styled(Row)`
    position: relative;
`;

const ImageWrapper = styled.div`
    border-radius: var(--radius-xs);
    flex-shrink: 0;
    height: 80px;
    overflow: hidden;
    width: 80px;
`;

const Thumbnail = styled(Image)`
    height: 100%;
    object-fit: cover;
    width: 100%;
`;

const TextContainer = styled(Column)`
    flex: 1;
    min-width: 0;
`;

const wrapperCss = css`
    width: 100%;
    ${pressActionEffect}
`;

const TierBadge = styled.div<{
    color?:string;
    backgroundColor?: string;
}>`
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 9px;
    font-weight: 500;
    color: ${({ color }) => color || colors.buttonText};
    background-color: ${({ backgroundColor }) => backgroundColor || colors.primary}
`;

export default CouponItem;
